<script setup>
import { ref, computed, watch, defineAsyncComponent, onUnmounted } from 'vue'
import { useLayersStore } from '@/stores/layers'
import { API, HOST } from '@/utils/http'
import { useRoute } from 'vue-router'
import ProjectFilters from './ProjectFilters.vue'
import { toast } from '@/components/ui/toast'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { Download, Filter, ChevronDown, ChevronUp } from 'lucide-vue-next'
import LayerExportDialog from '@/components/layers/LayerExportDialog.vue'
import { mergePolygons } from '@/utils/geometry'
import { useFeaturesFeed } from '@/composables/useFeaturesFeed'
import {
  Pagination,
  PaginationEllipsis,
  PaginationFirst,
  PaginationLast,
  PaginationList,
  PaginationListItem,
  PaginationNext,
  PaginationPrev,
} from '@/components/ui/pagination'

const FeaturesList = defineAsyncComponent(() => import('@/components/features/FeaturesList.vue'))

const props = defineProps({
  layer: {
    type: Object,
    required: true
  },
  activeTab: {
    type: String,
    required: true
  }
})

const route = useRoute()
const layersStore = useLayersStore()
const orgId = computed(() => route.params.org_id)
const layerId = computed(() => route.params.layer_id)
const exportDialogRef = ref(null)


const layerFilterConfig = computed(() => {
  return props.layer?.properties?.layer?.data?.filter?.config
})

const layerCode = computed(() => {
  return props.layer?.uuid
})

const {
  total,
  page,
  limit,
  isLoading,
  viewportOnly,
  showFilters,
  currentFilters,
  hasActiveFilters,
  displayFeatures,
  selectedRange,
  fetchFeatures,
  fetchDates,
  toggleViewportOnly,
  updateFilters: composableUpdateFilters,
  paginationUpdated,
  resetState,
} = useFeaturesFeed({ orgId, projectId: null, layerCode })

// Local hasActiveFilters override — collected data also checks layerFilterConfig
const localHasActiveFilters = computed(() => {
  return hasActiveFilters.value || (layerFilterConfig.value && layerFilterConfig.value.length > 0)
})

// Initial fetch when tab is data
watch(() => props.activeTab, (tab) => {
  if (tab === 'data') {
    fetchDates()
    // If there's a saved filter config, ProjectFilters will mount and
    // trigger fetchFeatures after applying initial filters.
    // Otherwise, fetch immediately.
    const hasFilterConfig = layerFilterConfig.value && layerFilterConfig.value.length > 0
    if (!hasFilterConfig) {
      fetchFeatures()
    }
  }
}, { immediate: true })

// Refetch when switching to a different layer
watch(layerCode, () => {
  resetState()
  fetchDates()
  if (props.activeTab === 'data') {
    fetchFeatures()
  }
})

// Filters panel is collapsed by default; user can expand manually

const openExportDialog = () => {
  if (exportDialogRef.value) {
    exportDialogRef.value.open()
  }
}

// Skip auto-save on initial mount (ProjectFilters emits update:filters during onMounted)
const hasUserInteracted = ref(false)

// Debounced auto-save for filter changes
let saveDebounceTimer = null
const debouncedSave = (filters) => {
  if (saveDebounceTimer) clearTimeout(saveDebounceTimer)
  saveDebounceTimer = setTimeout(() => {
    saveFilterChanges(filters)
  }, 1500)
}

onUnmounted(() => {
  if (saveDebounceTimer) clearTimeout(saveDebounceTimer)
})

const updateFilters = (filters) => {
  // On the initial mount-time emission from ProjectFilters, avoid a redundant fetch.
  // If there IS a saved filter config, we skipped fetchFeatures in the watcher so we
  // need the composable to fetch now. If there ISN'T, the watcher already fetched so
  // just set the filter state silently.
  if (!hasUserInteracted.value) {
    hasUserInteracted.value = true
    const hasFilterConfig = layerFilterConfig.value && layerFilterConfig.value.length > 0
    if (hasFilterConfig) {
      composableUpdateFilters(filters)
    }
    return
  }

  composableUpdateFilters(filters)

  // Auto-save filters to backend with debounce
  debouncedSave(filters)
}

const saveFilterChanges = async (filterData) => {
  if (!props.layer || props.layer.properties.type !== 4) return

  try {
    const filterConfig = {
      type: "AndFilter",
      config: []
    };

    if (filterData.projectIds && filterData.projectIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "project_id",
        config: filterData.projectIds.map(id => id.toString())
      });
    }

    if (filterData.templateIds && filterData.templateIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "template_id",
        config: filterData.templateIds.map(id => id.toString())
      });
    }

    if (filterData.memberIds && filterData.memberIds.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "member_id",
        config: filterData.memberIds.map(id => id.toString())
      });
    }

    if (filterData.searchText) {
      filterConfig.config.push({
        type: "TextFilter",
        field_name: "search",
        config: filterData.searchText
      });
    }

    if (filterData.isPublicData !== undefined) {
      filterConfig.config.push({
        type: "BoolFilter",
        field_name: "is_public",
        config: filterData.isPublicData
      });
    }

    if (filterData.dateRange && filterData.dateRange.start && filterData.dateRange.end) {
      filterConfig.config.push({
        type: "DateRangeFilter",
        field_name: "acquired",
        config: {
          gte: filterData.dateRange.start.toISOString(),
          lte: filterData.dateRange.end.toISOString()
        }
      });
    }

    if (filterData.geometries && filterData.geometries.length > 0) {
      const mergedGeometry = mergePolygons(filterData.geometries)
      if (mergedGeometry) {
        filterConfig.config.push({
          type: "GeometryFilter",
          field_name: "geometry",
          config: mergedGeometry
        });
      }
    }

    if (filterData.geometryTypes && filterData.geometryTypes.length > 0) {
      filterConfig.config.push({
        type: "ArrayFilter",
        field_name: "geometry_type",
        config: filterData.geometryTypes
      });
    }

    await API({
      method: 'PATCH',
      url: `${HOST}/org/${orgId.value}/layers/${layerId.value}/collected-data`,
      data: {
        filter: filterConfig
      }
    })

    // Update the layer in the store with the new filter
    const updatedLayer = {
      ...props.layer,
      properties: {
        ...props.layer.properties,
        layer: {
          ...props.layer.properties.layer,
          data: {
            filter: filterConfig
          }
        }
      }
    }

    layersStore.updateOrgLayer({
      orgId: orgId.value,
      layer: updatedLayer
    })

  } catch (error) {
    console.error('Error saving filters:', error)
    toast({
      title: 'Update failed',
      description: 'Failed to save filter settings',
      variant: 'destructive'
    });
  }
}


</script>

<template>
  <div>
    <!-- Data Tab Content -->
    <div v-if="activeTab === 'data'">
      <div class="px-3 py-2">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-2 text-muted-foreground">
            <span>Total: {{ total }}</span>
          </div>
          <Button
            @click="showFilters = !showFilters"
            variant="ghost"
            size="sm"
            class="text-primary hover:bg-primary/10 relative"
          >
            <Filter class="h-4 w-4 mr-1" />
            Filter
            <span v-if="localHasActiveFilters" class="ml-1 w-2 h-2 bg-primary rounded-full inline-block"></span>
            <component :is="showFilters ? ChevronUp : ChevronDown" class="h-4 w-4 ml-1" />
          </Button>
        </div>

        <div v-if="showFilters" class="border rounded-lg p-3 mt-2 bg-muted/50 space-y-3">
          <div class="flex items-center">
            <Switch :model-value="viewportOnly" @update:model-value="toggleViewportOnly" />
            <span class="text-sm font-medium ml-2">Show data in map area</span>
          </div>

          <ProjectFilters
            v-if="layer?.properties?.layer?.data"
            :key="layerCode"
            :initial-filter="layer?.properties?.layer?.data"
            :org-id="orgId"
            @update:filters="updateFilters"
          />


        </div>
      </div>

      <!-- Initial loading state -->
      <div v-if="isLoading && displayFeatures.length === 0" class="flex flex-col items-center justify-center py-12 text-muted-foreground">
        <svg class="animate-spin h-6 w-6 mb-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
        </svg>
        <span class="text-sm">Loading data...</span>
      </div>

      <!-- Data List -->
      <div v-if="displayFeatures.length > 0">
        <div class="relative">
          <div :class="{ 'opacity-50 transition-opacity duration-200': isLoading }">
            <FeaturesList :features="displayFeatures" />
          </div>
          <div v-if="isLoading" class="absolute inset-x-0 top-2 flex justify-center pointer-events-none">
            <span class="text-xs text-muted-foreground bg-background/80 px-2 py-1 rounded shadow-sm">Updating...</span>
          </div>
        </div>

        <div v-if="total > limit && !isLoading" class="p-3">
          <Pagination
            v-slot="{ page: currentPage }"
            :total="total"
            :sibling-count="1"
            show-edges
            :default-page="page"
            :items-per-page="limit"
            @update:page="paginationUpdated"
          >
            <PaginationList v-slot="{ items }" class="flex items-center gap-1">
              <PaginationFirst />
              <PaginationPrev />
              <template v-for="(item, index) in items">
                <PaginationListItem v-if="item.type === 'page'" :key="index" :value="item.value" as-child>
                  <Button class="w-10 h-10 p-0" :variant="item.value === currentPage ? 'default' : 'outline'">
                    {{ item.value }}
                  </Button>
                </PaginationListItem>
                <PaginationEllipsis v-else :key="item.type" :index="index" />
              </template>
              <PaginationNext />
              <PaginationLast />
            </PaginationList>
          </Pagination>
        </div>
      </div>
      <div v-else-if="!isLoading" class="p-4 text-center text-muted-foreground text-sm">
        No features found for the current filters.
      </div>

      <!-- Action Buttons -->
      <div class="flex justify-center gap-2 mt-4 mb-4">
        <Button
          @click="openExportDialog"
          :disabled="total === 0"
          variant="outline"
          size="sm"
          class="border-primary text-primary hover:bg-primary hover:text-white"
        >
          <Download class="h-4 w-4 mr-2" />
          Export Data
        </Button>
      </div>

      <!-- Export Dialog -->
      <LayerExportDialog
        ref="exportDialogRef"
        :org-id="orgId"
        :layer-code="layerCode"
        :date-range="selectedRange"
      />
    </div>

    <!-- AI Reports Tab Content -->
    <div v-if="activeTab === 'ai-reports'">
      <!-- TODO: AIReporting component not yet implemented -->
      <div class="text-sm text-muted-foreground p-4">AI Reporting coming soon...</div>
    </div>
  </div>
</template>
